/**
 * Order management system for limit orders, stop loss, and take profit
 */

export type OrderType = 'limit_buy' | 'limit_sell' | 'stop_loss' | 'take_profit' | 'dca';
export type OrderStatus = 'pending' | 'triggered' | 'executed' | 'cancelled' | 'expired';

export interface Order {
  id: string;
  type: OrderType;
  tokenAddress: string;
  tokenSymbol: string;
  // For limit orders
  triggerPrice: number; // USD price to trigger
  amount: number; // SOL for buys, tokens for sells
  // For DCA
  dcaInterval?: number; // minutes between purchases
  dcaRemaining?: number; // remaining purchases
  dcaTotal?: number; // total number of purchases
  dcaNextAt?: number; // next scheduled execution timestamp
  // Meta
  createdAt: number;
  expiresAt?: number;
  status: OrderStatus;
  executedAt?: number;
  executedTxHash?: string;
  walletAddress: string;
}

export interface OrderNotification {
  orderId: string;
  type: 'triggered' | 'executed' | 'expired';
  message: string;
  timestamp: number;
}

const ORDERS_KEY = 'skop_orders';
const NOTIFICATIONS_KEY = 'skop_order_notifications';

/**
 * Generate unique order ID
 */
function generateOrderId(): string {
  return `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get all orders from storage
 */
export function getOrders(): Order[] {
  try {
    const stored = localStorage.getItem(ORDERS_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Get orders for a specific token
 */
export function getOrdersForToken(tokenAddress: string): Order[] {
  return getOrders().filter(o => o.tokenAddress === tokenAddress);
}

/**
 * Get pending orders for a wallet
 */
export function getPendingOrders(walletAddress: string): Order[] {
  return getOrders().filter(o =>
    o.walletAddress === walletAddress && o.status === 'pending'
  );
}

/**
 * Create a new order
 */
export function createOrder(params: Omit<Order, 'id' | 'createdAt' | 'status'>): Order {
  const orders = getOrders();

  const newOrder: Order = {
    ...params,
    id: generateOrderId(),
    createdAt: Date.now(),
    status: 'pending',
  };

  orders.push(newOrder);
  localStorage.setItem(ORDERS_KEY, JSON.stringify(orders));

  return newOrder;
}

/**
 * Update order status
 */
export function updateOrderStatus(orderId: string, status: OrderStatus, txHash?: string): Order | null {
  const orders = getOrders();
  const index = orders.findIndex(o => o.id === orderId);

  if (index === -1) return null;

  orders[index].status = status;
  if (status === 'executed') {
    orders[index].executedAt = Date.now();
    if (txHash) orders[index].executedTxHash = txHash;
  }

  localStorage.setItem(ORDERS_KEY, JSON.stringify(orders));
  return orders[index];
}

/**
 * Cancel an order
 */
export function cancelOrder(orderId: string): boolean {
  const order = updateOrderStatus(orderId, 'cancelled');
  return order !== null;
}

/**
 * Delete an order permanently
 */
export function deleteOrder(orderId: string): boolean {
  const orders = getOrders();
  const filtered = orders.filter(o => o.id !== orderId);

  if (filtered.length === orders.length) return false;

  localStorage.setItem(ORDERS_KEY, JSON.stringify(filtered));
  return true;
}

/**
 * Check DCA orders that are due for execution
 */
export function checkDcaOrders(tokenAddress: string): Order[] {
  const orders = getOrders();
  const due: Order[] = [];
  const now = Date.now();

  for (const order of orders) {
    if (order.tokenAddress !== tokenAddress || order.status !== 'pending' || order.type !== 'dca') continue;

    // Check if DCA is due
    if (order.dcaNextAt && now >= order.dcaNextAt && order.dcaRemaining && order.dcaRemaining > 0) {
      due.push(order);

      addNotification({
        orderId: order.id,
        type: 'triggered',
        message: `DCA buy #${(order.dcaTotal || 0) - order.dcaRemaining + 1} triggered for ${order.tokenSymbol}`,
        timestamp: now,
      });
    }
  }

  return due;
}

/**
 * Execute a DCA purchase and schedule next one
 */
export function executeDcaPurchase(orderId: string, txHash?: string): Order | null {
  const orders = getOrders();
  const index = orders.findIndex(o => o.id === orderId);

  if (index === -1) return null;

  const order = orders[index];
  if (order.type !== 'dca' || !order.dcaRemaining || !order.dcaInterval) return null;

  order.dcaRemaining -= 1;
  order.executedAt = Date.now();
  if (txHash) order.executedTxHash = txHash;

  if (order.dcaRemaining > 0) {
    // Schedule next DCA
    order.dcaNextAt = Date.now() + order.dcaInterval * 60 * 1000;
  } else {
    // DCA complete
    order.status = 'executed';
    addNotification({
      orderId: order.id,
      type: 'executed',
      message: `DCA completed for ${order.tokenSymbol} (${order.dcaTotal} purchases)`,
      timestamp: Date.now(),
    });
  }

  localStorage.setItem(ORDERS_KEY, JSON.stringify(orders));
  return order;
}

/**
 * Check orders against current price and return triggered orders
 */
export function checkOrderTriggers(tokenAddress: string, currentPriceUsd: number): Order[] {
  const orders = getOrders();
  const triggered: Order[] = [];

  for (const order of orders) {
    if (order.tokenAddress !== tokenAddress || order.status !== 'pending') continue;

    // Check expiration
    if (order.expiresAt && Date.now() > order.expiresAt) {
      updateOrderStatus(order.id, 'expired');
      addNotification({
        orderId: order.id,
        type: 'expired',
        message: `${order.type} order for ${order.tokenSymbol} expired`,
        timestamp: Date.now(),
      });
      continue;
    }

    let shouldTrigger = false;

    switch (order.type) {
      case 'limit_buy':
        // Trigger when price drops to or below trigger price
        shouldTrigger = currentPriceUsd <= order.triggerPrice;
        break;
      case 'limit_sell':
        // Trigger when price rises to or above trigger price
        shouldTrigger = currentPriceUsd >= order.triggerPrice;
        break;
      case 'stop_loss':
        // Trigger when price drops to or below trigger price
        shouldTrigger = currentPriceUsd <= order.triggerPrice;
        break;
      case 'take_profit':
        // Trigger when price rises to or above trigger price
        shouldTrigger = currentPriceUsd >= order.triggerPrice;
        break;
    }

    if (shouldTrigger) {
      updateOrderStatus(order.id, 'triggered');
      triggered.push({ ...order, status: 'triggered' });

      addNotification({
        orderId: order.id,
        type: 'triggered',
        message: `${order.type.replace('_', ' ')} triggered for ${order.tokenSymbol} at $${currentPriceUsd.toFixed(6)}`,
        timestamp: Date.now(),
      });
    }
  }

  return triggered;
}

/**
 * Get notifications
 */
export function getNotifications(): OrderNotification[] {
  try {
    const stored = localStorage.getItem(NOTIFICATIONS_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Add a notification
 */
function addNotification(notification: OrderNotification): void {
  const notifications = getNotifications();
  notifications.unshift(notification);
  // Keep only last 50 notifications
  localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(notifications.slice(0, 50)));
}

/**
 * Clear notifications
 */
export function clearNotifications(): void {
  localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify([]));
}

/**
 * Format order type for display
 */
export function formatOrderType(type: OrderType): string {
  const labels: Record<OrderType, string> = {
    limit_buy: 'Limit Buy',
    limit_sell: 'Limit Sell',
    stop_loss: 'Stop Loss',
    take_profit: 'Take Profit',
    dca: 'DCA',
  };
  return labels[type];
}

/**
 * Get order type color
 */
export function getOrderTypeColor(type: OrderType): string {
  const colors: Record<OrderType, string> = {
    limit_buy: 'var(--success)',
    limit_sell: 'var(--warning)',
    stop_loss: 'var(--error)',
    take_profit: 'var(--success)',
    dca: 'var(--accent)',
  };
  return colors[type];
}
